import React, { useState } from 'react'
import { PacketStrategyGroup } from '@marketing-milk/interfaces'
import {
  PacketModalShell,
  modalGhostButtonStyle,
  modalInputStyle,
  modalPrimaryButtonStyle,
} from '../../components/PacketModalShell'
import { SurfacePalette, type } from '../../tokens'

type Props = {
  palette: SurfacePalette
  group: PacketStrategyGroup
  /** Every other theme in the packet — the possible homes for this one's ads. */
  destinations: PacketStrategyGroup[]
  deletionBlocked: boolean
  onDelete: (moveToGroupID: number) => void
  onClose: () => void
}

/**
 * Deleting a theme that still has ads. They go to a theme the AE picks, never
 * to "Not yet grouped": that bucket means "new since the last generation", and
 * filling it would surface Regenerate, which reshuffles the whole packet.
 */
export function DeleteThemeDialog({
  palette,
  group,
  destinations,
  deletionBlocked,
  onDelete,
  onClose,
}: Props) {
  const [destinationID, setDestinationID] = useState<number | null>(null)
  const count = group.ads.length
  const disabled = destinationID === null || deletionBlocked

  return (
    <PacketModalShell palette={palette} title={`Delete “${group.label}”`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            fontSize: type.size.body,
            color: palette.textMuted,
          }}
        >
          This theme has {count} {count === 1 ? 'ad' : 'ads'}. Move {count === 1 ? 'it' : 'them'}{' '}
          to:
          <select
            value={destinationID ?? ''}
            onChange={event => setDestinationID(Number(event.target.value))}
            style={modalInputStyle(palette)}
          >
            <option value="" disabled>
              Choose a theme
            </option>
            {destinations.map(destination => (
              <option key={destination.id} value={destination.id}>
                {destination.label}
              </option>
            ))}
          </select>
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onClose} style={modalGhostButtonStyle(palette)}>
            Cancel
          </button>
          <button
            type="button"
            disabled={disabled}
            title={deletionBlocked ? 'Wait for regeneration to finish' : undefined}
            onClick={() => {
              if (disabled || destinationID === null) return
              onDelete(destinationID)
              onClose()
            }}
            style={{
              ...modalPrimaryButtonStyle(palette),
              opacity: disabled ? 0.5 : 1,
            }}
          >
            Move ads and delete
          </button>
        </div>
      </div>
    </PacketModalShell>
  )
}
